import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { tablesAPI } from '../services/api';
import { customToast } from '../utils/toast';
import { useLanguage } from '../contexts/LanguageContext';
import { LimitGate } from '../components/FeatureGate';
import QRCode from 'qrcode';
import {
  Plus,
  Edit,
  Trash2,
  QrCode,
  Download,
  Copy,
  Check,
  Table2,
  Sparkles,
  Search,
  Filter,
  Share,
  Scan,
  AlertTriangle,
} from 'lucide-react';

const TableManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copiedTableId, setCopiedTableId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  // Fetch tables
  const { data: tablesData, isLoading } = useQuery(
    'tables',
    () => tablesAPI.getTables()
  );

  const tables = tablesData?.data || [];

  // Filter tables based on search
  const filteredTables = tables.filter(table =>
    table.table_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    table.qr_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mutations
  const createTableMutation = useMutation(
    (data) => tablesAPI.createTable(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tables');
        customToast.success(t('table.created', 'Table created successfully'));
        setShowModal(false);
      },
      onError: (error) => {
        customToast.error(t('table.createError', 'Failed to create table'));
      },
    }
  );

  const updateTableMutation = useMutation(
    (data) => tablesAPI.updateTable(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tables');
        customToast.success(t('table.updated', 'Table updated successfully'));
        setShowModal(false);
        setEditingTable(null);
      },
      onError: (error) => {
        customToast.error(t('table.updateError', 'Failed to update table'));
      },
    }
  );

  const deleteTableMutation = useMutation(
    (data) => tablesAPI.deleteTable(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tables');
        customToast.success(t('table.deleted', 'Table deleted successfully'));
        setShowDeleteConfirm(false);
        setTableToDelete(null);
      },
      onError: (error) => {
        customToast.error(t('table.deleteError', 'Failed to delete table'));
        setShowDeleteConfirm(false);
        setTableToDelete(null);
      },
    }
  );

  const handleCreateTable = (data) => {
    createTableMutation.mutate(data);
  };

  const handleUpdateTable = (data) => {
    updateTableMutation.mutate(data);
  };

  const handleDeleteTable = (table) => {
    setTableToDelete(table);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (tableToDelete) {
      deleteTableMutation.mutate({ id: tableToDelete.id });
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTableToDelete(null);
  };

  const generateQRCode = async (table) => {
    try {
      const customerUrl = `${window.location.origin}/customer/${table.qr_code}`;
      const qrCodeDataURL = await QRCode.toDataURL(customerUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1e40af',
          light: '#ffffff'
        }
      });
      setQrCodeUrl(qrCodeDataURL);
      setSelectedTable(table);
    } catch (error) {
      customToast.error(t('table.qrError', 'Failed to generate QR code'));
    }
  };

  const downloadQRCode = () => {
    if (!selectedTable) return;
    const link = document.createElement('a');
    link.download = `table-${selectedTable.table_number}-qr.png`;
    link.href = qrCodeUrl;
    link.click();
    customToast.success(t('table.qrDownloaded', 'QR code downloaded successfully'));
  };

  const copyTableUrl = async (table) => {
    const customerUrl = `${window.location.origin}/customer/${table.qr_code}`;
    try {
      await navigator.clipboard.writeText(customerUrl);
      setCopiedTableId(table.id);
      customToast.success(t('table.urlCopied', 'URL copied to clipboard'));
      setTimeout(() => setCopiedTableId(null), 2000);
    } catch (error) {
      customToast.error(t('table.copyError', 'Failed to copy URL'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 lg:p-8">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="max-w-7xl mx-auto py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              {t('tableManagement.title', 'Table Management')}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
                {t('tableManagement.heading', 'Manage Your Tables')}
              </h1>
              <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-2xl">
                {t('tableManagement.description', 'Create tables and generate QR codes for customers to access your digital menu and place orders.')}
              </p>
            </div>
            {/* Mobile Add Button */}
            <div className="sm:hidden">
              <LimitGate
                type="tables"
                current={tables.length}
              >
                <button
                  onClick={() => {
                    setEditingTable(null);
                    setShowModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">{t('tableManagement.addTable', 'Add Table')}</span>
                </button>
              </LimitGate>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('tableManagement.searchPlaceholder', 'Search tables...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 w-full"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button className="flex items-center gap-2 bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-gray-200 hover:bg-gray-100 transition-all duration-300">
                <Filter className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                <span className="text-xs sm:text-sm font-medium">
                  {t('common.filter', 'Filter')}
                </span>
              </button>

              {/* Desktop Add Button */}
              <div className="hidden sm:block">
                <LimitGate
                  type="tables"
                  current={tables.length}
                >
                  <button
                    onClick={() => {
                      setEditingTable(null);
                      setShowModal(true);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="text-sm sm:text-base">{t('tableManagement.addTable', 'Add Table')}</span>
                  </button>
                </LimitGate>
              </div>
            </div>
          </div>
        </div>

        {/* Tables Grid */}
        {isLoading ? (
          <div className="flex justify-center py-8 sm:py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTables.length === 0 ? (
          <div className="text-center py-12 sm:py-16 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Table2 className="h-6 w-6 sm:h-10 sm:w-10 text-blue-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              {t('tableManagement.noTablesFound', 'No Tables Found')}
            </h3>
            <p className="text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base px-4">
              {searchTerm
                ? t('tableManagement.noSearchResults', 'No tables match your search. Try adjusting your search terms.')
                : t('tableManagement.emptyState', 'Create your first table to start generating QR codes for customers.')
              }
            </p>
            {!searchTerm && (
              <LimitGate
                type="tables"
                current={tables.length}
              >
                <button
                  onClick={() => {
                    setEditingTable(null);
                    setShowModal(true);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  {t('tableManagement.createFirstTable', 'Create Your First Table')}
                </button>
              </LimitGate>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredTables.map((table) => (
              <div
                key={table.id}
                className="group bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2 border border-gray-100"
              >
                <div className="p-4 sm:p-6">
                  {/* Table Header */}
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white">
                        <Table2 className="h-3 w-3 sm:h-5 sm:w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                          {t('tableManagement.tableNumber', 'Table')} {table.table_number}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {t('common.id', 'ID')}: {table.qr_code.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* QR Code Preview */}
                  <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-gray-200">
                    <div className="w-full h-20 sm:h-32 bg-white rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                      <QrCode className="h-5 w-5 sm:h-8 sm:w-8 text-gray-400" />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 sm:space-y-3">
                    <button
                      onClick={() => generateQRCode(table)}
                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg sm:rounded-xl font-medium hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 text-xs sm:text-sm"
                    >
                      <Scan className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('tableManagement.generateQR', 'Generate QR Code')}
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => copyTableUrl(table)}
                        className={`py-2 px-2 sm:px-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-1 text-xs ${copiedTableId === table.id
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                          }`}
                      >
                        {copiedTableId === table.id ? (
                          <>
                            <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>{t('common.copied', 'Copied!')}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>{t('common.copyURL', 'Copy URL')}</span>
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => {
                          setEditingTable(table);
                          setShowModal(true);
                        }}
                        className="py-2 px-2 sm:px-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-1 text-xs border border-gray-200"
                      >
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span>{t('common.edit', 'Edit')}</span>
                      </button>
                    </div>

                    <button
                      onClick={() => handleDeleteTable(table)}
                      className="w-full py-2 sm:py-2.5 px-3 sm:px-4 bg-red-50 text-red-700 rounded-lg sm:rounded-xl font-medium hover:bg-red-100 transition-all duration-300 flex items-center justify-center gap-2 text-xs sm:text-sm border border-red-200"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('tableManagement.deleteTable', 'Delete Table')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        {tables.length > 0 && (
          <div className="mt-6 sm:mt-8 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
              <div className="p-3 sm:p-4 bg-blue-50 rounded-lg sm:rounded-xl">
                <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-1">{tables.length}</div>
                <div className="text-xs sm:text-sm text-blue-700 font-medium">
                  {t('tableManagement.totalTables', 'Total Tables')}
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-green-50 rounded-lg sm:rounded-xl">
                <div className="text-xl sm:text-2xl font-bold text-green-600 mb-1">{tables.length}</div>
                <div className="text-xs sm:text-sm text-green-700 font-medium">
                  {t('tableManagement.activeQRCodes', 'Active QR Codes')}
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-purple-50 rounded-lg sm:rounded-xl">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-1">100%</div>
                <div className="text-xs sm:text-sm text-purple-700 font-medium">
                  {t('tableManagement.readyToUse', 'Ready to Use')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {qrCodeUrl && selectedTable && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-100 mx-auto">
              <div className="text-center mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white mx-auto mb-3 sm:mb-4">
                  <Scan className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {t('tableManagement.tableQRCode', 'Table {tableNumber} QR Code', { tableNumber: selectedTable.table_number })}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  {t('tableManagement.qrDescription', 'Scan this code to access the digital menu')}
                </p>
              </div>

              <div className="text-center mb-4 sm:mb-6 p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl">
                <img src={qrCodeUrl} alt="QR Code" className="mx-auto w-40 h-40 sm:w-48 sm:h-48" />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={downloadQRCode}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                  {t('tableManagement.downloadQR', 'Download QR Code')}
                </button>

                <button
                  onClick={() => copyTableUrl(selectedTable)}
                  className="w-full py-2.5 sm:py-3 px-4 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base border border-gray-200"
                >
                  <Share className="h-4 w-4 sm:h-5 sm:w-5" />
                  {t('tableManagement.shareURL', 'Share Table URL')}
                </button>

                <button
                  onClick={() => {
                    setQrCodeUrl('');
                    setSelectedTable(null);
                  }}
                  className="w-full py-2.5 sm:py-3 px-4 bg-white text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-50 transition-all duration-300 border border-gray-300 text-sm sm:text-base"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && tableToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-100 mx-auto">
              <div className="text-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-2xl flex items-center justify-center text-white mx-auto mb-3 sm:mb-4">
                  <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {t('tableManagement.deleteConfirm', 'Delete Table?')}
                </h3>
                <p className="text-gray-600 mb-2 text-sm sm:text-base">
                  {t('tableManagement.deleteMessage', 'Are you sure you want to delete Table ')} {tableToDelete.table_number} ?
                </p>
                <p className="text-xs sm:text-sm text-red-600 font-medium">
                  {t('tableManagement.deleteWarning', 'This action cannot be undone. All associated QR codes will become invalid.')}
                </p>
              </div>

              <div className="flex space-x-2 sm:space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 py-2.5 sm:py-3 px-4 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm sm:text-base"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteTableMutation.isLoading}
                  className="flex-1 py-2.5 sm:py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {deleteTableMutation.isLoading ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('common.deleting', 'Deleting...')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                      {t('common.delete', 'Delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table Modal */}
        {showModal && (
          <TableModal
            table={editingTable}
            onClose={() => {
              setShowModal(false);
              setEditingTable(null);
            }}
            onSubmit={editingTable ? handleUpdateTable : handleCreateTable}
            isLoading={createTableMutation.isLoading || updateTableMutation.isLoading}
          />
        )}
      </div>
    </div>
  );
};

// Table Modal Component
const TableModal = ({ table, onClose, onSubmit, isLoading }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    table_number: table?.table_number || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (table) {
      data.id = table.id;
    }
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl border border-gray-100 mx-auto">
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white mx-auto mb-3 sm:mb-4">
            <Table2 className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
            {table ? t('tableManagement.editTable', 'Edit Table') : t('tableManagement.createTable', 'Create New Table')}
          </h3>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            {table ? t('tableManagement.editDescription', 'Update your table details') : t('tableManagement.createDescription', 'Add a new table to generate QR codes')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label htmlFor="table_number" className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
              {t('tableManagement.tableNumberLabel', 'Table Number *')}
            </label>
            <input
              type="text"
              id="table_number"
              name="table_number"
              value={formData.table_number}
              onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
              className="block w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 text-sm sm:text-base"
              placeholder={t('tableManagement.tableNumberPlaceholder', 'e.g., 1, A1, Window Table, Outdoor 2')}
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              {t('tableManagement.tableNumberHelp', 'This will be displayed to customers when they scan the QR code')}
            </p>
          </div>

          <div className="flex justify-end space-x-2 sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm sm:text-base"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2 text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {table ? t('common.updating', 'Updating...') : t('common.creating', 'Creating...')}
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                  {table ? t('common.updateTable', 'Update Table') : t('common.createTable', 'Create Table')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TableManagement;